/**
 * A file frame that links a file where it is (plan 14): its content's
 * `local` names the link, the path and the computer it is on. The path is
 * for people and agents; this computer opens or reads only through its own
 * list of links (`desktop/local-files.ts`), never by a path from content.
 */
import { useEffect, useState } from "react";
import {
  knownComputer,
  thisComputer,
  type LinkedFile,
  type ThisComputer,
} from "@/desktop/local-files";
import { fileExtension, fileKind } from "@/lib/file-kinds";
import { isTauri } from "@/lib/is-tauri";

export interface LocalLink {
  linkId: string;
  path: string;
  machineId: string;
  machineName: string;
}

const LINK_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function text(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === "string" ? value : "";
}

/** The link in a file frame's content (or card), or null when it has none. */
export function localLink(content: unknown): LocalLink | null {
  if (!content || typeof content !== "object" || Array.isArray(content)) {
    return null;
  }
  const local = (content as Record<string, unknown>).local;
  if (!local || typeof local !== "object" || Array.isArray(local)) return null;
  const record = local as Record<string, unknown>;
  const link = {
    linkId: text(record, "linkId"),
    path: text(record, "path"),
    machineId: text(record, "machineId"),
    machineName: text(record, "machineName"),
  };
  return LINK_ID.test(link.linkId) && link.machineId ? link : null;
}

/**
 * Where a linked file is from this window: on this computer, elsewhere
 * (another computer, or the web), or not known yet while the desktop app
 * says which computer this is.
 */
export type LinkPlace = "here" | "elsewhere" | "pending";

export function linkPlace(
  link: LocalLink,
  computer: ThisComputer | null | undefined,
): LinkPlace {
  if (computer === undefined) return "pending";
  return computer?.machineId === link.machineId ? "here" : "elsewhere";
}

/** The link in `content` when its file is on this computer, as known now. */
export function linkHere(content: unknown): LocalLink | null {
  const link = localLink(content);
  return link && linkPlace(link, knownComputer()) === "here" ? link : null;
}

/**
 * This computer in the desktop app, null on the web or when the app cannot
 * say, undefined while it is asked.
 */
export function useThisComputer(): ThisComputer | null | undefined {
  const [computer, setComputer] = useState<ThisComputer | null | undefined>(
    () => (isTauri() ? undefined : null),
  );
  useEffect(() => {
    if (!isTauri()) return;
    let live = true;
    void thisComputer().then(
      (found) => live && setComputer(found),
      () => live && setComputer(null),
    );
    return () => {
      live = false;
    };
  }, []);
  return computer;
}

/**
 * The content of a file linked on this computer: its name, kind (`pdf`, else
 * its extension), size when it has one (a macOS package has none) and link.
 */
export function linkedFileContent(
  file: LinkedFile,
  computer: ThisComputer,
): Record<string, unknown> {
  const pdf = fileKind({ name: file.name, type: "" }) === "pdf";
  return {
    name: file.name,
    kind: pdf ? "pdf" : fileExtension(file.name),
    sizeBytes: file.sizeBytes,
    local: {
      linkId: file.linkId,
      path: file.path,
      machineId: computer.machineId,
      machineName: computer.machineName,
    },
  };
}
